// packages/core/src/learning/index.ts
// ─────────────────────────────────────────────────────────────────────────
// Public entry for the Learn data layer.
// Apps import everything from '@nisk/core/learning'.
// ─────────────────────────────────────────────────────────────────────────
// 1) Re-export new editorial layers
export * from './FigureRegistry';
export * from './SchoolExhibitMeta';
export * from './SchoolMeta';
export * from './DebateMeta';

import { LEARNING_MODULES } from './LearningData';
import type {
	AssessmentId,
	Flashcard,
	LearningModule,
	Lesson,
	ModuleAssessment,
	Question,
	SchoolOfThought,
	Tier,
	LessonKind,
} from './LearningTypes';

export * from './LearningTypes';
export * from './CurriculumMeta';
export * from './kindToScenarioType';
export * from './readTime';
// export * from './SchoolMeta';
export * from './PlaylistMeta';
export { LEARNING_MODULES };

export function getSchoolLessonCount(school: SchoolOfThought): number {
	let n = 0;
	for (const module of LEARNING_MODULES) {
		for (const lesson of module.lessons) {
			if (lesson.schoolsOfThought.includes(school)) n += 1;
		}
	}
	return n;
}

// ── Module lookups ───────────────────────────────────────────────────────
export function getAllModules(): LearningModule[] {
	return LEARNING_MODULES;
}

export function getModule(moduleId: string): LearningModule | undefined {
	return LEARNING_MODULES.find((m) => m.id === moduleId);
}

// The tier grid is the Core Path spine ONLY. School (Lens Pack) modules carry
// a tier to satisfy the LearningModule type, but must never appear on the Map.
// Routing through getMapModules() (surface 'map'/undefined) enforces that in
// one place, so every Map consumer is correct by construction. See Product
// Decision 1 in LearningData.ts — separate progress tracks.
export function getModulesByTier(tier: Tier): LearningModule[] {
	return getMapModules()
		.filter((m) => m.tier === tier)
		.sort((a, b) => a.order - b.order);
}

// ── Lesson lookups ───────────────────────────────────────────────────────
export function getLesson(
	moduleId: string,
	lessonId: string,
): Lesson | undefined {
	return getModule(moduleId)?.lessons.find((l) => l.id === lessonId);
}

export function getLessonById(
	lessonId: string,
): { module: LearningModule; lesson: Lesson } | undefined {
	for (const module of LEARNING_MODULES) {
		const lesson = module.lessons.find((l) => l.id === lessonId);
		if (lesson) return { module, lesson };
	}
	return undefined;
}

export function getNextLesson(
	moduleId: string,
	lessonId: string,
): Lesson | undefined {
	const lessons = getModule(moduleId)?.lessons ?? [];
	const idx = lessons.findIndex((l) => l.id === lessonId);
	if (idx === -1 || idx + 1 >= lessons.length) return undefined;
	return lessons[idx + 1];
}

// ── Flashcards ───────────────────────────────────────────────────────────
export function getModuleFlashcards(moduleId: string): Flashcard[] {
	return getModule(moduleId)?.lessons.flatMap((l) => l.flashcards) ?? [];
}

// ── Module assessment (V2.3 — reads attached assessment object) ──────────
export function getModuleAssessment(
	moduleId: string,
): ModuleAssessment | undefined {
	return getModule(moduleId)?.assessment;
}

export function getAssessmentQuestions(
	moduleId: string,
	assessmentId: AssessmentId,
): Question[] {
	const assessment = getModuleAssessment(moduleId);
	if (!assessment) return [];
	switch (assessmentId) {
		case 'cq':
			return assessment.checkpointQuiz;
		case 'final':
			return assessment.finalTest;
	}
}

// ── Storage key helpers ──────────────────────────────────────────────────
export function assessmentStorageKey(
	moduleId: string,
	assessmentId: AssessmentId,
): string {
	return `quiz:${moduleId}:${assessmentId}`;
}

// ── Filtering ────────────────────────────────────────────────────────────
export function getLessonsBySchool(
	school: SchoolOfThought,
): Array<{ module: LearningModule; lesson: Lesson }> {
	const out: Array<{ module: LearningModule; lesson: Lesson }> = [];
	for (const module of LEARNING_MODULES) {
		for (const lesson of module.lessons) {
			if (lesson.schoolsOfThought.includes(school))
				out.push({ module, lesson });
		}
	}
	return out;
}

// ── Validation ───────────────────────────────────────────────────────────
export function validateCurriculum(): {
	ok: boolean;
	errors: string[];
	warnings: string[];
} {
	const errors: string[] = [];
	const warnings: string[] = [];

	const allLessonIds = new Set<string>();
	const allFlashcardIds = new Set<string>();
	const allQuestionIds = new Set<string>();

	// First pass — collect IDs
	for (const module of LEARNING_MODULES) {
		for (const lesson of module.lessons) {
			if (allLessonIds.has(lesson.id))
				errors.push(`Duplicate lesson id: ${lesson.id}`);
			allLessonIds.add(lesson.id);

			for (const f of lesson.flashcards) {
				if (allFlashcardIds.has(f.id))
					errors.push(
						`Duplicate flashcard id: ${f.id} (in ${lesson.id})`,
					);
				allFlashcardIds.add(f.id);
			}
		}
		if (module.assessment) {
			const all = [
				...module.assessment.checkpointQuiz,
				...module.assessment.finalTest,
			];
			for (const q of all) {
				if (allQuestionIds.has(q.id))
					errors.push(
						`Duplicate question id: ${q.id} (in ${module.id})`,
					);
				allQuestionIds.add(q.id);
			}
		}
	}

	// Second pass — semantic checks
	for (const module of LEARNING_MODULES) {
		const isStub = module.lessons.length === 0;
		if (isStub) {
			warnings.push(`${module.id} is empty (not yet generated)`);
			continue;
		}

		for (const lesson of module.lessons) {
			// Prereq / related lesson integrity
			for (const prereq of lesson.prerequisites) {
				if (!allLessonIds.has(prereq))
					errors.push(
						`${lesson.id} → unknown prerequisite: ${prereq}`,
					);
			}
			for (const rel of lesson.relatedLessons) {
				if (!allLessonIds.has(rel))
					warnings.push(
						`${lesson.id} → unknown related lesson: ${rel}`,
					);
			}

			// Flashcards: exactly 3
			if (lesson.flashcards.length !== 3) {
				warnings.push(
					`${lesson.id} has ${lesson.flashcards.length} flashcards (expected exactly 3)`,
				);
			}

			// Flashcard roles: each lesson should carry exactly one of each
			// memory-anchor role (main_idea, watch_out, use_it). Roles are
			// optional in the type for legacy data, but new content must set
			// them; this surfaces gaps + duplicates without hard-failing.
			{
				const roles = lesson.flashcards
					.map((f) => f.role)
					.filter((r): r is NonNullable<typeof r> => Boolean(r));
				const missing = lesson.flashcards.length - roles.length;
				if (missing > 0) {
					warnings.push(
						`${lesson.id} has ${missing} flashcard(s) missing a role (expected main_idea/watch_out/use_it)`,
					);
				}
				for (const expected of [
					'main_idea',
					'watch_out',
					'use_it',
				] as const) {
					const count = roles.filter((r) => r === expected).length;
					if (
						roles.length === lesson.flashcards.length &&
						count !== 1
					) {
						warnings.push(
							`${lesson.id} has ${count} '${expected}' flashcards (expected exactly 1)`,
						);
					}
				}
			}

			// Block structure: must end with key_takeaway
			const lastBlock = lesson.blocks[lesson.blocks.length - 1];
			if (!lastBlock || lastBlock.type !== 'key_takeaway') {
				warnings.push(
					`${lesson.id} should end with a key_takeaway block`,
				);
			}

			// Audio script length
			if (lesson.audioScript) {
				const wc = lesson.audioScript.trim().split(/\s+/).length;
				if (wc < 180 || wc > 1000) {
					warnings.push(
						`${lesson.id} audioScript is ${wc} words (expected 500–900)`,
					);
				}
			}
		}

		// Module assessment checks.
		// School-surface modules (surface: 'school') intentionally carry no
		// checkpointQuiz/finalTest — assessments live on the 12 Core Path
		// modules only. Skip the "no assessment authored" warning for them,
		// but still validate question counts + moduleId integrity IF a school
		// module ever does author an assessment.
		const isSchoolModule = module.surface === 'school';
		if (
			!isSchoolModule &&
			(!module.assessment ||
				(module.assessment.checkpointQuiz.length === 0 &&
					module.assessment.finalTest.length === 0))
		) {
			warnings.push(`${module.id} has no assessment authored yet`);
		} else if (module.assessment) {
			const a = module.assessment;
			if (a.checkpointQuiz.length < 6 || a.checkpointQuiz.length > 8) {
				warnings.push(
					`${module.id} checkpointQuiz has ${a.checkpointQuiz.length} questions (expected 6–8)`,
				);
			}
			if (a.finalTest.length < 10 || a.finalTest.length > 12) {
				warnings.push(
					`${module.id} finalTest has ${a.finalTest.length} questions (expected 10–12)`,
				);
			}
			if (a.moduleId !== module.id) {
				errors.push(
					`${module.id} assessment.moduleId mismatch: ${a.moduleId}`,
				);
			}
		}
	}

	return { ok: errors.length === 0, errors, warnings };
}

// ─────────────────────────────────────────────────────────────────────────
// ADDITIONS to packages/core/src/learning/index.ts
//
// Append these to the existing barrel file. I don't have your full index.ts
// in the project mirror, so this is given as additions to apply — not a
// full replacement. Order them alongside the existing exports and helpers.
// ─────────────────────────────────────────────────────────────────────────

// 3) The Map shows only the progressive spine — modules with surface 'map'
//    or undefined. School modules are filtered out so they don't appear on
//    the tier grid.
export function getMapModules(): LearningModule[] {
	return LEARNING_MODULES.filter((m) => (m.surface ?? 'map') === 'map');
}

// 4) The single school module for a school (if any has been authored).
//    A school has at most one home module by convention.
export function getSchoolModule(
	school: SchoolOfThought,
): LearningModule | undefined {
	return LEARNING_MODULES.find(
		(m) => m.surface === 'school' && m.homeSchool === school,
	);
}

// 5) Resolve the effective kind of a lesson given its module context.
//    Defaults:
//      - module.surface 'map'    → 'map'
//      - module.surface 'school' → 'school-core' unless lessonKind says otherwise
function effectiveLessonKind(
	module: LearningModule,
	lesson: Lesson,
): LessonKind {
	if (lesson.lessonKind) return lesson.lessonKind;
	return module.surface === 'school' ? 'school-core' : 'map';
}

// 6) Get the core (3–7 must-reads) of a school — pulled from that school's
//    native module, lessons with lessonKind 'school-core' (or undefined,
//    which defaults to core inside a school module).
export function getSchoolCoreLessons(
	school: SchoolOfThought,
): Array<{ module: LearningModule; lesson: Lesson }> {
	const mod = getSchoolModule(school);
	if (!mod) return [];
	return mod.lessons
		.filter((l) => effectiveLessonKind(mod, l) === 'school-core')
		.map((lesson) => ({ module: mod, lesson }));
}

// 7) The deeper / niche lessons of a school.
export function getSchoolExtraLessons(
	school: SchoolOfThought,
): Array<{ module: LearningModule; lesson: Lesson }> {
	const mod = getSchoolModule(school);
	if (!mod) return [];
	return mod.lessons
		.filter((l) => effectiveLessonKind(mod, l) === 'school-extra')
		.map((lesson) => ({ module: mod, lesson }));
}

// 9) Resolve a list of lesson ids to { module, lesson } pairs, preserving
//    order and silently skipping ids that don't exist. Used by
//    SchoolExhibitMeta.featuredLessonIds, Playlist lessonIds, etc.
export function resolveLessonRefs(
	lessonIds: string[],
): Array<{ module: LearningModule; lesson: Lesson }> {
	const out: Array<{ module: LearningModule; lesson: Lesson }> = [];
	for (const id of lessonIds) {
		for (const module of LEARNING_MODULES) {
			const lesson = module.lessons.find((l) => l.id === id);
			if (lesson) {
				out.push({ module, lesson });
				break;
			}
		}
	}
	return out;
}
